/**
 * Sistema de i18n custom (PR #106).
 *
 * Por que não usar react-i18next?
 * - Adiciona ~30KB ao bundle (mobile-first é prioritário, CLAUDE.md §2.1)
 * - APIs assíncronas + Suspense complicam fluxo simples
 * - Pra MVP, lookup direto em JSON com fallback PT é suficiente
 *
 * Uso:
 *   import { useT, setIdioma } from "@/lib/i18n";
 *   const t = useT();
 *   return <h1>{t("home.titulo")}</h1>;
 *
 * Persistência:
 *   localStorage["acordei_idioma"] = "pt" | "en"
 *   Default: detecta navigator.language (pt-* → pt, senão en)
 *
 * Adicionar nova chave: editar pt.json + en.json simultâneo.
 */
import { useEffect, useState, useSyncExternalStore } from "react";
import ptDict from "../i18n/pt.json";
import enDict from "../i18n/en.json";

export type Idioma = "pt" | "en";

const STORAGE_KEY = "acordei_idioma";
const DICTS: Record<Idioma, Record<string, string>> = {
  pt: ptDict as Record<string, string>,
  en: enDict as Record<string, string>,
};

// Subscribers pra useSyncExternalStore (notifica components quando muda)
const subscribers = new Set<() => void>();

function detectarIdioma(): Idioma {
  if (typeof localStorage !== "undefined") {
    const salvo = localStorage.getItem(STORAGE_KEY);
    if (salvo === "pt" || salvo === "en") return salvo;
  }
  if (typeof navigator !== "undefined") {
    const lang = navigator.language?.toLowerCase() || "";
    if (lang.startsWith("pt")) return "pt";
    if (lang.startsWith("en")) return "en";
  }
  return "pt"; // default
}

let _idiomaAtual: Idioma = detectarIdioma();

function notifyAll() {
  subscribers.forEach((cb) => cb());
}

export function getIdioma(): Idioma {
  return _idiomaAtual;
}

export function setIdioma(novo: Idioma): void {
  if (novo !== "pt" && novo !== "en") return;
  if (_idiomaAtual === novo) return;
  _idiomaAtual = novo;
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(STORAGE_KEY, novo);
  }
  // Atualiza <html lang="..."> pra acessibilidade
  if (typeof document !== "undefined") {
    document.documentElement.lang = novo;
  }
  notifyAll();
}

function subscribe(cb: () => void): () => void {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

/**
 * Traduz chave dot-notation. Fallback: chave própria se não achar.
 *
 * Substituição de placeholders: passar segundo arg como dict.
 *   t("home.bem_vindo", { nome: "Maria" })
 *   → "Bem-vinda, {nome}" → "Bem-vinda, Maria"
 */
export function traduzir(chave: string, vars?: Record<string, string | number>): string {
  const dict = DICTS[_idiomaAtual] || DICTS.pt;
  let valor = dict[chave];
  if (valor === undefined) {
    // Fallback pra PT
    valor = DICTS.pt[chave];
  }
  if (valor === undefined) {
    // Sem tradução: retorna a própria chave (visível em dev pra completar)
    return chave;
  }
  if (vars) {
    valor = valor.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`));
  }
  return valor;
}

/**
 * Hook React — re-render quando idioma muda.
 *
 * Usa useSyncExternalStore (React 18+) pra subscrever sem state local.
 */
export function useT(): (chave: string, vars?: Record<string, string | number>) => string {
  const idioma = useSyncExternalStore(
    subscribe,
    getIdioma,
    getIdioma, // server snapshot = same (SSR not used)
  );
  // idioma é usado pra forçar re-render quando muda; traduzir() lê _idiomaAtual
  void idioma;
  return traduzir;
}

/**
 * Hook React — retorna idioma atual + setter (pra switcher na UI).
 */
export function useIdioma(): [Idioma, (i: Idioma) => void] {
  const idioma = useSyncExternalStore(subscribe, getIdioma, getIdioma);
  return [idioma, setIdioma];
}

/**
 * Inicialização — chamada em main.tsx pra setar <html lang> no boot.
 */
export function inicializarI18n(): void {
  if (typeof document !== "undefined") {
    document.documentElement.lang = _idiomaAtual;
  }
}
